import React from 'react';
import styles from './styles.scss';
const { Fragment, useState } = React;

import {CaptureScheduleModalActionTypes, Page} from '../../types/capture-schedule-modal';

import CaptureScheduleModalStore from '../../rx-stores/capture-schedule-modal';
import { Sensor } from '../../types/api';

import useRxStore from '../../utils/use-rx-store';
import useRxDispatch from '../../utils/use-rx-dispatch';
import { useRequiredSessionToken } from '../../utils/session-token';

import {
  AppBar,
  AppBarContext,
  AppBarSection,
  AppBarTitle,
  Button,
  Modal,
} from '@densityco/ui';

import ProcessingPage from './page-processing';
import SummaryPage from './page-summary-page';
import GlobalConfigurationPage from './page-global-configuration';
import CaptureSchedulesPage from './page-capture-schedules';

const {useEffect} = React;

function getPageWidth(page: Page): number {
  if (page === Page.GLOBAL_CONFIGURATION) {
    return 600;
  } else if (page === Page.SUMMARY) {
    return 800;
  } else if (page === Page.PROCESSING) {
    return 800;
  } else {
    return 1200;
  }
}

export type CaptureScheduleModalSensor = {
  serial_number: Sensor['serial_number'],
  link_id: string,
  name: string,
  organization: {
    id: string,
    name: string,
  },
};

type CaptureScheduleModalProps = {
  sensors: Array<CaptureScheduleModalSensor>,

  open: boolean,
  onDismiss: () => void,
};

const CaptureScheduleModal: React.FunctionComponent<CaptureScheduleModalProps> = (props) => {
  const { open, onDismiss, sensors } = props;
  const jwtToken = useRequiredSessionToken();

  const state = useRxStore(CaptureScheduleModalStore);
  const dispatch = useRxDispatch();

  // TODO: This is a hack that needs to be upstreamed into density ui in order
  // to properly let the modal open and close without the animation behavior
  // acting weirdly.
  const [ openModal, setOpenModal ] = useState(true);
  const [ renderModal, setRenderModal ] = useState(false);
  useEffect(() => {
    let unmounted = false;
    if (open) {
      setRenderModal(true);
      setOpenModal(true);
    } else {
      setOpenModal(false);
      setTimeout(() => {
        if (!unmounted) {
          setRenderModal(false);
          dispatch({ type: CaptureScheduleModalActionTypes.CAPTURE_SCHEDULE_MODAL_RESET });
        }
      }, 500);
    }

    return () => { unmounted = true; }
  }, [ open ]);

  if (renderModal) {
    if (sensors.length === 0) {
      return (
        <Modal
          visible={openModal}
          onEscape={onDismiss}
          onBlur={onDismiss}
          width={500}
        >
          <AppBar>
            <AppBarTitle>Error: No Sensors Found</AppBarTitle>
          </AppBar>
          <div className={styles.noSensorsWrapper}>
            <p>
              None of the spaces selected have any sensors within them. Please ensure:
            </p>
            <ul>
              <li>Each space that you are scheduling captures for has at least one doorway linked to it.</li>
              <li>
                Each doorway has an sensor linked to it - you can tell this by ensuring that the
                "sensor id" field in core cucumber each doorway is filled out.
              </li>
            </ul>
          </div>
          <AppBarContext.Provider value="BOTTOM_ACTIONS">
            <AppBar>
              <AppBarSection />
              <AppBarSection>
                <Button variant="filled" onClick={() => onDismiss()}>Close</Button>
              </AppBarSection>
            </AppBar>
          </AppBarContext.Provider>
        </Modal>
      );
    }

    const sensorsWithNullLink = sensors.filter(i => i.link_id === null);
    if (sensorsWithNullLink.length > 0) {
      return (
        <Modal
          visible={openModal}
          onEscape={onDismiss}
          onBlur={onDismiss}
          width={500}
        >
          <AppBar>
            <AppBarTitle>Error: Doorway not attached to space</AppBarTitle>
          </AppBar>
          <div className={styles.noSensorsWrapper}>
            <p>
              The below sensors do not have a doorway to space link, and therefore, cannot have captures scheduled on them:
            </p>
            <ul>
              {sensorsWithNullLink.map(i => (
                <li key={i.serial_number}>{i.name} (serial number: {i.serial_number})</li>
              ))}
            </ul>
          </div>
          <AppBarContext.Provider value="BOTTOM_ACTIONS">
            <AppBar>
              <AppBarSection />
              <AppBarSection>
                <Button variant="filled" onClick={() => onDismiss()}>Close</Button>
              </AppBarSection>
            </AppBar>
          </AppBarContext.Provider>
        </Modal>
      );
    }

    return (
      <Modal
        visible={openModal}
        onEscape={onDismiss}
        onBlur={onDismiss}
        width={getPageWidth(state.page)}
      >
        <AppBar>
          <AppBarTitle>Create Capture Schedules</AppBarTitle>
        </AppBar>

        {state.page === Page.CAPTURE_SCHEDULES ? (
          <CaptureSchedulesPage onDismiss={onDismiss} state={state} dispatch={dispatch} />
        ) : null}

        {state.page === Page.GLOBAL_CONFIGURATION ? (
          <GlobalConfigurationPage onDismiss={onDismiss} state={state} dispatch={dispatch} />
        ) : null}

        {state.page === Page.SUMMARY ? (
          <SummaryPage
            onDismiss={onDismiss}
            state={state}
            dispatch={dispatch}
            sensors={sensors}
          />
        ) : null}

        {state.page === Page.PROCESSING ? (
          <ProcessingPage
            onDismiss={onDismiss}
            state={state}
            dispatch={dispatch}
            sensors={sensors}
            jwtToken={jwtToken}
          />
        ) : null}
      </Modal>
    );
  } else {
    return null;
  }
};

export default CaptureScheduleModal;
